/**
 * Sentence chunker for streaming TTS.
 *
 * Accumulates SSE tokens and emits complete sentences on boundary detection.
 * Boundaries: . ! ? \n\n : (before lists)
 * Minimum chunk size: 10 chars to prevent fragments.
 */

const SENTENCE_BOUNDARY = /[.!?]\s|[.!?]$|\n\n|:\s*\n/g;
const MIN_CHUNK_LENGTH = 10;

/** Tokens that should not be spoken (tool/markdown artifacts). */
function isSkippable(token: string): boolean {
  // Skip tool-use markers and pure markdown formatting
  if (/^```/.test(token) || /^---$/.test(token.trim())) return true;
  return false;
}

export interface SentenceChunker {
  /** Feed a token. Returns all complete sentences found, or empty array. */
  feed(token: string): string[];
  /** Flush remaining buffer at end of stream. */
  flush(): string | null;
}

export function createSentenceChunker(): SentenceChunker {
  let buffer = '';

  return {
    feed(token: string): string[] {
      if (isSkippable(token)) return [];

      buffer += token;

      const sentences: string[] = [];

      // Reset lastIndex since the regex has the global flag
      SENTENCE_BOUNDARY.lastIndex = 0;

      let match: RegExpExecArray | null;
      let searchFrom = 0;

      // Loop to extract ALL complete sentences from the buffer in one pass
      while ((match = SENTENCE_BOUNDARY.exec(buffer)) !== null) {
        const endIdx = match.index + match[0].length;
        const candidate = buffer.slice(searchFrom, endIdx).trim();

        if (candidate.length >= MIN_CHUNK_LENGTH) {
          sentences.push(candidate);
          searchFrom = endIdx;
          // Update lastIndex so the next exec() continues after what we consumed
          SENTENCE_BOUNDARY.lastIndex = endIdx;
        }
      }

      // Advance the buffer past all consumed sentences
      if (searchFrom > 0) {
        buffer = buffer.slice(searchFrom);
      }

      return sentences;
    },

    flush(): string | null {
      const remaining = buffer.trim();
      buffer = '';
      return remaining.length >= MIN_CHUNK_LENGTH ? remaining : remaining.length > 0 ? remaining : null;
    },
  };
}
